import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from './ui/sonner';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^[+\d][\d\s\-()]{6,19}$/;

const initialFormErrors = {
  name: '',
  email: '',
  mobile: ''
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState(initialFormErrors);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }

    if (submitStatus.type) {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const validateForm = () => {
    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedMobile = formData.mobile.trim();

    const nextErrors = {
      name: '',
      email: '',
      mobile: ''
    };

    if (!trimmedName) {
      nextErrors.name = 'Name is required.';
    } else if (trimmedName.length < 2) {
      nextErrors.name = 'Name must be at least 2 characters.';
    }

    if (!trimmedEmail) {
      nextErrors.email = 'Email is required.';
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      nextErrors.email = 'Please enter a valid email address.';
    }

    if (!trimmedMobile) {
      nextErrors.mobile = 'Mobile number is required.';
    } else if (!MOBILE_REGEX.test(trimmedMobile)) {
      nextErrors.mobile = 'Please enter a valid mobile number.';
    }

    setFormErrors(nextErrors);

    if (nextErrors.name || nextErrors.email || nextErrors.mobile) {
      return null;
    }

    return {
      name: trimmedName,
      email: trimmedEmail,
      mobile: trimmedMobile,
      message: formData.message.trim()
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    const payload = validateForm();

    if (!payload) {
      toast.error('Please review the required fields.', {
        description: 'Name, Email, and Mobile must be valid before submission.'
      });
      setSubmitStatus({
        type: 'error',
        message: 'Please correct the highlighted fields and try again.'
      });
      setIsSubmitting(false);
      return;
    }

    try {
      if (!API_BASE_URL) {
        throw new Error('Backend URL is missing.');
      }

      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const responseError = Array.isArray(result.detail)
          ? result.detail[0]?.msg
          : result.detail;
        throw new Error(responseError || result.message || 'Unable to submit your inquiry.');
      }

      toast.success('Thank you for your inquiry!', {
        description: 'We will get back to you within 24 hours.'
      });

      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your inquiry! We will get back to you within 24 hours.'
      });

      setFormData({ name: '', email: '', mobile: '', message: '' });
      setFormErrors(initialFormErrors);
    } catch (error) {
      toast.error('Submission failed', {
        description: error.message || 'Please try again in a moment.'
      });

      setSubmitStatus({
        type: 'error',
        message: error.message || 'Submission failed. Please try again in a moment.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-section" id="contact" data-testid="contact-section">
      <div className="container">
        <div className="contact-wrapper">
          <div className="contact-info-block">
            <div className="section-header">
              <span className="section-label" data-testid="contact-form-section-label">GET IN TOUCH</span>
              <h2 className="section-title" data-testid="contact-form-title">Ready to Manufacture Your Next Innovation?</h2>
              <p className="contact-intro" data-testid="contact-form-intro-text">
                Submit your requirements today and our engineering team will connect with you.
              </p>
            </div>
          </div>
          
          <div className="contact-form-wrapper">
            <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form" noValidate>
              <div className="form-group">
                <label htmlFor="name" className="form-label">Name *</label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Your full name"
                  data-testid="contact-form-name-input"
                />
                {formErrors.name && (
                  <p className="form-helper-error" data-testid="contact-form-name-error">
                    {formErrors.name}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Email *</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="you@company.com"
                  data-testid="contact-form-email-input"
                />
                {formErrors.email && (
                  <p className="form-helper-error" data-testid="contact-form-email-error">
                    {formErrors.email}
                  </p>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="mobile" className="form-label">Mobile Number *</label>
                <Input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  required
                  value={formData.mobile}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Your contact number"
                  data-testid="contact-form-mobile-input"
                />
                {formErrors.mobile && (
                  <p className="form-helper-error" data-testid="contact-form-mobile-error">
                    {formErrors.mobile}
                  </p>
                )}
              </div>
              
              <div className="form-group">
                <label htmlFor="message" className="form-label">Issue / Need (Optional)</label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Tell us about your project requirements..."
                  rows={4}
                  data-testid="contact-form-message-input"
                />
              </div>
              
              <Button 
                type="submit" 
                className="primary-cta submit-btn"
                disabled={isSubmitting}
                data-testid="contact-form-submit-button"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              </Button>

              {submitStatus.message && (
                <p
                  className={`form-status-message ${submitStatus.type === 'success' ? 'success' : 'error'}`}
                  data-testid={submitStatus.type === 'success' ? 'contact-form-success-message' : 'contact-form-error-message'}
                >
                  {submitStatus.message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
